import React from 'react';
import { motion } from 'framer-motion';

const testimonials = [
  {
    text: "iMANE's senior practitioners understood our procurement complexity from day one. The transformation delivered results we did not think were possible in that timeframe. The same people who scoped our engagement delivered it - no junior handoffs, no surprises.",
    name: 'Head of Procurement',
    role: 'Manufacturing Enterprise, Australia',
    initials: 'HP',
  },
  {
    text: "We had tried larger firms before and struggled with constant team rotations and senior partners who disappeared after sign-off. iMANE was completely different. Contract digitization went from 0% to 100% in 4 months.",
    name: 'Chief Procurement Officer',
    role: 'Professional Services Firm, India',
    initials: 'CP',
  },
  {
    text: "Outstanding supplier lifecycle transformation. The team's deep domain knowledge accelerated our timeline significantly. We went from fragmented supplier data to a fully governed qualification process within 7 months.",
    name: 'VP Operations',
    role: 'Energy & Utilities, Middle East',
    initials: 'VO',
  },
  {
    text: "The boutique model works. iMANE brought exactly the right expertise without any of the overhead of a large consulting firm. Our guided buying adoption went from near zero to 70% - a result that directly impacts our bottom line.",
    name: 'IT & Procurement Director',
    role: 'Retail Sector, APAC',
    initials: 'IP',
  },
  {
    text: "iMANE's managed services team has been exceptional post go-live. System uptime, adoption support, continuous optimization - they remain genuine partners to our business long after implementation concluded.",
    name: 'Group CFO',
    role: 'Telecom, India',
    initials: 'GC',
  },
  {
    text: "What impressed us most was the outcome-focused model. iMANE were invested in our success, not just billing hours. Procurement processing time reduced by over 50% in 5 months across our multi-entity operation.",
    name: 'Senior VP Finance & Procurement',
    role: 'FMCG, Multi-region',
    initials: 'SV',
  },
  {
    text: "The integration work between our procurement platform and ERP was complex, but iMANE handled it with expertise that clearly came from years of doing exactly this. Invoice automation results exceeded our targets.",
    name: 'Chief Information Officer',
    role: 'Healthcare & Pharma, UAE',
    initials: 'CI',
  },
  {
    text: "We evaluated several firms. iMANE's deep domain focus was immediately clear. No generalism, no bloated teams, no overcomplication. Exactly the specialist partner our procurement transformation needed.",
    name: 'Chief Procurement Officer',
    role: 'Automotive, Southeast Asia',
    initials: 'CP',
  },
  {
    text: "The regional procurement consolidation program was ambitious. iMANE delivered a unified model across four countries in 9 months. The quality of work from a boutique firm genuinely surprised even our most skeptical stakeholders.",
    name: 'Group Procurement Director',
    role: 'Automotive, Southeast Asia',
    initials: 'GP',
  },
];

function TestimonialsColumn({ testimonials: items, duration = 15, className = '' }) {
  return (
    <div className={`overflow-hidden ${className}`}>
      <motion.div
        animate={{ translateY: '-50%' }}
        transition={{
          duration,
          repeat: Infinity,
          ease: 'linear',
          repeatType: 'loop',
        }}
        className="flex flex-col gap-5"
      >
        {[...Array(2)].map((_, dupIdx) => (
          <React.Fragment key={dupIdx}>
            {items.map((t, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl border border-brand-border bg-white shadow-sm max-w-xs w-full"
              >
                <p className="text-slate-700 text-sm leading-relaxed font-source-sans mb-5">
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-brand-navy flex items-center justify-center text-white text-xs font-bold font-lexend flex-shrink-0">
                    {t.initials}
                  </div>
                  <div>
                    <div className="font-lexend font-semibold text-sm text-brand-navy leading-tight">
                      {t.name}
                    </div>
                    <div className="text-xs text-slate-500 font-source-sans leading-tight mt-0.5">
                      {t.role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </motion.div>
    </div>
  );
}

const col1 = testimonials.slice(0, 3);
const col2 = testimonials.slice(3, 6);
const col3 = testimonials.slice(6, 9);

export default function TestimonialsSection() {
  return (
    <section className="py-20 lg:py-28 bg-brand-navy border-y border-slate-700" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="text-center max-w-xl mx-auto mb-12"
        >
          <div className="inline-block border border-slate-600 px-4 py-1 rounded-full text-xs font-medium text-slate-300 mb-5 font-source-sans">
            Client Voices
          </div>
          <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-white tracking-tight mb-4">
            What our clients say
          </h2>
          <p className="text-slate-300 text-base font-source-sans">
            Senior practitioners. Real engagements. Measurable outcomes.
          </p>
        </motion.div>

        {/* Columns with fade mask */}
        <div
          className="flex justify-center gap-5 overflow-hidden"
          style={{
            maskImage: 'linear-gradient(to bottom, transparent, black 15%, black 85%, transparent)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 15%, black 85%, transparent)',
            maxHeight: '700px',
          }}
        >
          <TestimonialsColumn testimonials={col1} duration={18} />
          <TestimonialsColumn testimonials={col2} duration={22} className="hidden md:block" />
          <TestimonialsColumn testimonials={col3} duration={20} className="hidden lg:block" />
        </div>
      </div>
    </section>
  );
}
